import _ from 'lodash'
import { useState } from 'react'
import { Control, Controller, useWatch } from 'react-hook-form'
import { t } from '@lingui/macro'

import { Box, IconButton, TextField } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import BuildIcon from '@mui/icons-material/Build'

import QuillController from '@/components/ui/inputs/QuillController'

import { isQuillContentEmpty } from '../lib/messageHelpers'
import { ChatMessageFormData, useChatMessageForm } from '../model/chatMessageForm'

type MessageInputProps = {
  chatRoomId?: string
  onMarkAsRead?: (chatRoomId: string) => Promise<void>
  onSendMessage: (message: string) => Promise<void>
}

export function MessageInput({ chatRoomId, onMarkAsRead, onSendMessage }: MessageInputProps) {
  const { control, handleSubmit, reset, setValue } = useChatMessageForm()

  const [onlyText, setOnlyText] = useState(true)

  const handleSend = async (data: { message: string }) => {
    if (data.message && !isQuillContentEmpty(data.message)) {
      await onSendMessage(data.message)
      reset()
      if (chatRoomId) {
        await onMarkAsRead?.(chatRoomId)
      }
    }
  }

  return (
    <Box
      sx={{
        p: 1,
        borderTop: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        flexShrink: 0,
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(handleSend)}
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1,
          width: '100%',
        }}
      >
        {onlyText ? (
          <Controller
            control={control}
            name="message"
            render={({ field }) => (
              <TextField
                {...field}
                placeholder={t`Type your message...`}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    minHeight: '80px',
                    alignItems: 'flex-start',
                  },
                }}
                variant="outlined"
              />
            )}
          />
        ) : (
          <QuillController
            control={control}
            name="message"
            sx={{
              '.ql-header': {
                '.ql-picker-options': {
                  overflow: 'auto',
                  maxHeight: '90px',
                },
              },
              '.ql-editor': {
                img: {
                  maxWidth: '200px',
                  maxHeight: '200px',
                },
              },
            }}
          />
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <IconButton
            onClick={() => {
              setOnlyText(!onlyText)
              setValue('message', '')
            }}
            sx={{
              height: '40px',
              width: '40px',
              background: (theme) => theme.palette.warning.main,
              color: 'white',
              '&:hover': {
                background: (theme) => theme.palette.primary.dark,
              },
            }}
          >
            <BuildIcon fontSize="small" />
          </IconButton>

          <SendButton control={control} />
        </Box>
      </Box>
    </Box>
  )
}

function SendButton({ control }: { control: Control<ChatMessageFormData> }) {
  const message = useWatch({ control, name: 'message' })
  return (
    <IconButton
      type="submit"
      sx={{
        height: '40px',
        width: '40px',
        background: (theme) => theme.palette.primary.main,
        color: 'white',
        '&:hover': {
          background: (theme) => theme.palette.primary.dark,
        },
      }}
      disabled={isQuillContentEmpty(message)}
    >
      <SendIcon fontSize="small" />
    </IconButton>
  )
}
